import { isValidObjectId } from "@/lib/utils/input-validator";

/**
 * True when `value` is an opaque id from a dynamic `optionSource` catalog that
 * the catalog does not contain — i.e. the referenced room / offering / email
 * template / import batch is gone, and rendering the stored value would put a
 * bare ObjectId in front of the user.
 *
 * Deliberately narrow on both axes:
 *
 *  - **`dynamicLabels` must be present.** Only `optionSource` fields carry it.
 *    For a custom SELECT the stored value IS human text ("green"), so falling
 *    back to it is correct and is pinned by TCCSingleSelect.orphan.test.tsx —
 *    that fallback exists because blanking an orphan let the next pick silently
 *    overwrite a stored value.
 *  - **The value must look like an ObjectId.** `emailedSendNames` is an
 *    optionSource field whose values are free-text campaign names; those are
 *    displayable as-is and must never be masked.
 *
 * Callers must check the catalog's pending state FIRST: while the source query
 * is in flight the catalog is empty for a reason that says nothing about the
 * stored value, and claiming "deleted" there would be a worse lie than the hex.
 */
export function isDanglingDynamicId(
  value: string,
  optionsMap: Map<string, string>,
  dynamicLabels?: Record<string, string>,
): boolean {
  if (!dynamicLabels) return false;
  if (optionsMap.has(value)) return false;
  return isValidObjectId(value);
}

/**
 * Whether a stored value should render blank because its catalog has not loaded
 * yet. Same ObjectId gate as above, so free-text and custom-select values keep
 * rendering immediately instead of flickering empty on every mount.
 */
export function isUnresolvedWhilePending(
  value: string,
  optionsMap: Map<string, string>,
  dynamicLabels?: Record<string, string>,
  optionsPending?: boolean,
): boolean {
  if (!optionsPending || !dynamicLabels) return false;
  if (optionsMap.has(value)) return false;
  return isValidObjectId(value);
}
